import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2Icon, RotateCcwIcon, SendIcon, ClockIcon, UserIcon, BuildingIcon, MessageSquareIcon, PaperclipIcon, ExternalLinkIcon, CircleCheckIcon } from "lucide-react";
import { TopBar } from "./TopBar";
import { ELM, BRAND } from "../lib/data";
type OrderState = "internal-review" | "customer-review" | "approved";
const stateMeta: Record<OrderState, {
  label: string;
  tone: string;
  note: string;
}> = {
  "internal-review": {
    label: "Internal Review",
    tone: "bg-amber-50 text-amber-700 ring-amber-200",
    note: "Awaiting operations sign-off before it goes to the client."
  },
  "customer-review": {
    label: "Out for Customer Review",
    tone: "bg-blue-50 text-blue-700 ring-blue-200",
    note: "Sent to Bob Carter for approval. Awaiting client response."
  },
  approved: {
    label: "Approved",
    tone: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    note: "Client approved. Cleared to schedule the campaign launch."
  }
};
const timeline = [{
  label: "Order received",
  meta: "Mon, 9:12 AM · Auto-routed to Listing Concierge",
  done: true
}, {
  label: "Assigned to Dana Lopez",
  meta: "Mon, 9:14 AM",
  done: true
}, {
  label: "Creative designed",
  meta: "Wed, 4:40 PM · 3 ad concepts",
  done: true
}, {
  label: "In review",
  meta: "Now",
  done: false
}];
export function AdminOrderDetail({
  onBack,
  onHome,
  onOpenTracker
}: {
  onBack: () => void;
  onHome: () => void;
  onOpenTracker: () => void;
}) {
  const [state, setState] = useState<OrderState>("internal-review");
  const meta = stateMeta[state];
  return <div className="min-h-full w-full bg-neutral-50 text-neutral-900" data-id="element-12" data-mp="1caipp66v235z">
      <TopBar onHome={onHome} onBack={onBack} backLabel="Board" title="Order Detail" data-id="element-13" data-mp="1c860q92oqsjf" />

      <main className="mx-auto max-w-[1100px] px-5 py-6 sm:px-8" data-id="element-14" data-mp="1h9lm628rz12c">
        <button onClick={onBack} className="mb-4 text-sm font-medium text-neutral-500 transition hover:text-neutral-900" data-id="element-15" data-mp="12d4996kxtedo">
          ← Back to order routing board
        </button>

        {/* Header */}
        <div className="flex flex-col gap-4 rounded-2xl border border-neutral-200 bg-white p-5 sm:flex-row sm:items-center" data-id="element-16" data-mp="o7cd4yah4oec">
          <img src={ELM.hero} alt={ELM.address} className="h-24 w-full rounded-xl object-cover sm:h-20 sm:w-28" data-id="element-17" data-mp="jej8sbix3i2s" />
          <div className="min-w-0 flex-1" data-id="element-18" data-mp="eeep6cmpq78c">
            <div className="flex flex-wrap items-center gap-2" data-id="element-19" data-mp="9ifxaf26e736">
              <h1 className="font-heading text-xl font-bold tracking-tight" data-id="element-20" data-mp="175euqs0ozj24">Marketing Package — {ELM.address}</h1>
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${meta.tone}`} data-id="element-21" data-mp="126ljz7csi65n">
                {state === "approved" ? <CircleCheckIcon className="h-3.5 w-3.5" data-id="element-22" data-mp="x5ec78and6yz" /> : <ClockIcon className="h-3.5 w-3.5" data-id="element-23" data-mp="s8nvqjuxk9d1" />}
                {meta.label}
              </span>
            </div>
            <p className="mt-1 text-sm text-neutral-500" data-id="element-24" data-mp="3hyj1gau2xdmr">Order #o-1042 · {ELM.type} · {ELM.price}</p>
            <p className="mt-0.5 text-sm text-neutral-500" data-id="element-25" data-mp="3cxbyqq6sn4my">{meta.note}</p>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.5fr_1fr]" data-id="element-26" data-mp="1shnj8m52j9y7">
          {/* Left column */}
          <div className="space-y-6" data-id="element-27" data-mp="1xg77wub1qzu9">
            {/* Admin actions */}
            <section className="rounded-2xl border border-neutral-200 bg-white p-5" data-id="element-28" data-mp="22e0azeh1zdqb">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-400" data-id="element-29" data-mp="274yv6p55wvyu">Order actions</h2>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row" data-id="element-30" data-mp="2c4v0qsticvgi">
                <ActionButton primary disabled={state === "customer-review"} onClick={() => setState("customer-review")} icon={<SendIcon className="h-4 w-4" data-id="element-32" data-mp="2l5t8nm72hn4d" />} label="Send for customer review" data-id="element-31" data-mp="2c6o5u3oh37me" />
                <ActionButton onClick={() => setState("internal-review")} icon={<RotateCcwIcon className="h-4 w-4" data-id="element-34" data-mp="qtblsfq54p09" />} label="Reclaim for changes" data-id="element-33" data-mp="2q1qj5bvnfz98" />
                <ActionButton onClick={() => setState("approved")} icon={<CheckCircle2Icon className="h-4 w-4" data-id="element-36" data-mp="2h8cls74972ii" />} label="Mark approved" success data-id="element-35" data-mp="2m6xe7kd9khwe" />
              </div>
              <button onClick={onOpenTracker} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{
              color: BRAND.accentText
            }} data-id="element-37" data-mp="2v72ivmr2p26v">
                <ExternalLinkIcon className="h-4 w-4" data-id="element-38" data-mp="23ebj52a6kvcv" /> Preview the client-facing order tracker
              </button>
            </section>

            {/* Creatives */}
            <section className="rounded-2xl border border-neutral-200 bg-white p-5" data-id="element-39" data-mp="2cakhedmf9nxf">
              <div className="flex items-center justify-between" data-id="element-40" data-mp="27hhma11zy6f4">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-400" data-id="element-41" data-mp="1jsgiws80tlgp">Deliverables</h2>
                <span className="text-xs text-neutral-400" data-id="element-42" data-mp="1frwq20uhd40d">3 ad creatives</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3" data-id="element-43" data-mp="15mwqpmbpcqgf">
                {["https://cdn.magicpatterns.com/patterns/generated-images/4eb851a1-a057-496c-aba6-4cc474cfb1d9.jpg", "https://cdn.magicpatterns.com/patterns/generated-images/91aa69a2-cda6-47af-8aa7-2491a2a0cdda.jpg", "https://cdn.magicpatterns.com/patterns/generated-images/dc89f424-70f5-404d-9456-7e8196945870.jpg"].map((src, i) => <img key={i} src={src} alt={`Creative ${i + 1}`} className="aspect-square w-full rounded-lg object-cover" data-id="element-44" data-mp="1tcr17x8ju2xi" data-mpi={i} />)}
              </div>
            </section>

            {/* Internal notes */}
            <section className="rounded-2xl border border-neutral-200 bg-white p-5" data-id="element-45" data-mp="1je1tgxad9yuz">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-400" data-id="element-46" data-mp="30l5mmehq3du">Internal notes</h2>
              <div className="mt-4 space-y-3" data-id="element-47" data-mp="7zxt99d6rehh">
                <Note author="Dana Lopez" role="Listing Concierge" text="Creatives finalized. Recommend leading with the Just Listed concept for the feed." time="Wed, 4:42 PM" data-id="element-48" data-mp="80ppmmelxg8t" />
                <Note author="Ops Bot" role="Automation" text="SLA healthy — 26h of buffer remaining before the Thursday launch window." time="Thu, 8:00 AM" data-id="element-49" data-mp="25obawufpjpfn" />
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2" data-id="element-50" data-mp="1vxyv1fiu8kfu">
                <MessageSquareIcon className="h-4 w-4 text-neutral-400" data-id="element-51" data-mp="1qz5efe898dbg" />
                <input placeholder="Add an internal note…" className="flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400" data-id="element-52" data-mp="1mwqzv6bmbzpz" />
                <PaperclipIcon className="h-4 w-4 text-neutral-400" data-id="element-53" data-mp="1hxxslvmxd4y9" />
              </div>
            </section>
          </div>

          {/* Right column */}
          <div className="space-y-6" data-id="element-54" data-mp="3cbysmju7nxzx">
            <section className="rounded-2xl border border-neutral-200 bg-white p-5" data-id="element-55" data-mp="3cax4bdo6pf09">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-400" data-id="element-56" data-mp="37ddnq740kq55">Assignment</h2>
              <dl className="mt-4 space-y-3 text-sm" data-id="element-57" data-mp="3ev3g27j1i9rd">
                <Row icon={<BuildingIcon className="h-4 w-4" data-id="element-59" data-mp="3jug2ws9wvgty" />} label="Department" value="Listing Concierge" data-id="element-58" data-mp="3juok2wd2h8ay" />
                <Row icon={<UserIcon className="h-4 w-4" data-id="element-61" data-mp="3oleq8btbhf6p" />} label="Owner" value="Dana Lopez" data-id="element-60" data-mp="3ol4yaiopoxyd" />
                <Row icon={<UserIcon className="h-4 w-4" data-id="element-63" data-mp="3tl12ahxespdx" />} label="Agent" value="Bob Carter" data-id="element-62" data-mp="3tlaqbzyw8mgz" />
                <Row icon={<ClockIcon className="h-4 w-4" data-id="element-65" data-mp="2vxslh08cs4i9" />} label="SLA / ETA" value="Thu, 2:00 PM" data-id="element-64" data-mp="2vxitj73qzn9x" />
              </dl>
            </section>

            <section className="rounded-2xl border border-neutral-200 bg-white p-5" data-id="element-66" data-mp="30wlxspbwlpgu">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-400" data-id="element-67" data-mp="35udr9ns75jn5">Activity</h2>
              <ol className="mt-4 space-y-4" data-id="element-68" data-mp="3av1j2bo924if">
                {timeline.map((step, i) => <li key={i} className="flex gap-3" data-id="element-69" data-mp="l4695u6eyjd1" data-mpi={i}>
                    <div className="flex flex-col items-center" data-id="element-70" data-mp="q3sktz6lfrjq">
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full ${step.done ? "text-white" : "border-2 border-dashed border-neutral-300 text-neutral-400"}`} style={step.done ? {
                    backgroundColor: BRAND.accent
                  } : undefined} data-id="element-71" data-mp="q27smg7r4kp1">
                        {step.done ? <CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-72" data-mp="1g7g0hl6pgtlq" /> : <ClockIcon className="h-3 w-3" data-id="element-73" data-mp="1g5lgqa7677j8" />}
                      </span>
                      {i < timeline.length - 1 && <span className="mt-1 h-full w-px flex-1 bg-neutral-200" data-id="element-74" data-mp="1twroetklsbh7" />}
                    </div>
                    <div className="pb-1" data-id="element-75" data-mp="1pz28l8nqyck9">
                      <p className={`text-sm font-semibold ${step.done ? "text-neutral-900" : "text-neutral-500"}`} data-id="element-76" data-mp="23x0l08iibyx4">{step.label}</p>
                      <p className="text-xs text-neutral-400" data-id="element-77" data-mp="1yy75kajntxub">{step.meta}</p>
                    </div>
                  </li>)}
              </ol>
            </section>
          </div>
        </div>
      </main>
    </div>;
}
function ActionButton({
  label,
  icon,
  onClick,
  primary,
  success,
  disabled
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
  success?: boolean;
  disabled?: boolean;
}) {
  const base = "flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed";
  return <motion.button whileTap={{
    scale: disabled ? 1 : 0.97
  }} onClick={onClick} disabled={disabled} className={`${base} ${primary ? "text-white" : success ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50"}`} style={primary ? {
    backgroundColor: BRAND.accent
  } : undefined} data-id="element-78" data-mp="2be6ves7vuu5b">
      {icon} {label}
    </motion.button>;
}
function Row({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between" data-id="element-79" data-mp="3mvyiiwg072ok">
      <dt className="flex items-center gap-2 text-neutral-500" data-id="element-80" data-mp="3muncsr2zzkut">
        <span className="text-neutral-400" data-id="element-81" data-mp="fnkmu427nqdd">{icon}</span> {label}
      </dt>
      <dd className="font-semibold text-neutral-900" data-id="element-82" data-mp="kfu9nb814abf">{value}</dd>
    </div>;
}
function Note({
  author,
  role,
  text,
  time
}: {
  author: string;
  role: string;
  text: string;
  time: string;
}) {
  return <div className="rounded-xl bg-neutral-50 p-3" data-id="element-83" data-mp="aozlu2f4x2nv">
      <div className="flex items-center gap-2" data-id="element-84" data-mp="apsjmdw48htv">
        <span className="text-sm font-semibold text-neutral-900" data-id="element-85" data-mp="yek8oweesk2f">{author}</span>
        <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-500" data-id="element-86" data-mp="13e6hmtn7fe5f">{role}</span>
      </div>
      <p className="mt-1 text-sm text-neutral-700" data-id="element-87" data-mp="oge60cks39wi">{text}</p>
      <p className="mt-1 text-xs text-neutral-400" data-id="element-88" data-mp="tfhbtz8l1k5j">{time}</p>
    </div>;
}